import React, { FunctionComponent, ReactElement, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from '../components/loader';
import { Dashboard } from '../routes/dashboard';
import { fetchDashboardJSON } from '../helpers/requestHelper';
import { track, eventNames } from '../helpers/userTracking';
import { checkIfPasswordIsValid } from '../helpers/checkIfPasswordIsValid';
import { Error } from '../routes/error';
import { Expired } from '../routes/expired';
import { Password } from '../routes/password';
import { Passwordless } from '../routes/passwordless';

const ONE_MINUTE = 60 * 1000;
const FIVE_MINUTES = 5 * ONE_MINUTE;
const ONE_HOUR = 60 * ONE_MINUTE;
const REFETCH_CANCEL_BUFFER_TIME = 20 * 60 * 1000;

const trackDashboardIncorrectPassword = () => track(eventNames.DASHBOARD_INCORRECT_PASSWORD);
const trackDashboardOpened = (passwordSet: ('Y' | 'N')) => track(eventNames.DASHBOARD_OPENED, { password_set: passwordSet });

const SharedDashboardContainer: FunctionComponent<{ baseUrl: string }> = (props): ReactElement => {
	const { baseUrl } = props;
	const { sharedDashboardUuid } = useParams<{ sharedDashboardUuid: string; }>();

	const [dashboardChecked, setDashboardChecked] = useState(false);
	const [dashboardValid, setDashboardValid] = useState(false);
	const [passwordNeeded, setPasswordNeeded] = useState(false);
	const [password, setPassword] = useState('');
	const [expired, setExpired] = useState(false);
	const [showDashboard, setShowDashboard] = useState(false);
	const [dashboardJSON, setDashboardJSON] = useState(null);

	const [dashboardFetchTimeoutId, setDashboardFetchTimeoutId] = useState<null | NodeJS.Timeout>(null);
	const [lastUpdatedIntervalId, setLastUpdatedIntervalId] = useState<null | NodeJS.Timer>(null);
	const [minutesSinceLastFetch, setMinutesSinceLastFetch] = useState<number>();
	const [abortController, setAbortController] = useState<AbortController | null>(null);
	const windowBlurTimestampRef = useRef<null | number>(null);

	// Handling password submission
	const handlePasswordSubmission = (password: string) => {
		setDashboardChecked(false);
		setPassword(password);
		setShowDashboard(true);
	};

	// Handling passwordless submission
	const handlePasswordlessSubmission = () => {
		setShowDashboard(true);
		trackDashboardOpened('N');
	};

	const updateMinutesSinceLastFetch = () => setMinutesSinceLastFetch(minutesSinceLastFetch => minutesSinceLastFetch as number + 1);

	const fetchAndProcessDashboardJSON = useCallback((skipAddingView = false) => {
		const newAbortControllerInstance = new AbortController();

		setAbortController(newAbortControllerInstance);

		fetchDashboardJSON({
			baseUrl,
			password,
			sharedDashboardUuid,
			signal: newAbortControllerInstance?.signal,
			skipAddingView
		})
			.then((responseDashboardJSON) => {
				setDashboardJSON(responseDashboardJSON as SetStateAction<null>);
				setDashboardValid(true);

				const windowBlurTimestamp = windowBlurTimestampRef.current;

				// If window isn't blurred or has been blurred for less than REFETCH_CANCEL_BUFFER_TIME, refetch the dashboard
				if (windowBlurTimestamp === null || Date.now() - windowBlurTimestamp < REFETCH_CANCEL_BUFFER_TIME) {
					const timeoutDuration = responseDashboardJSON.resourceType === 'live-dashboard-meta' ? FIVE_MINUTES : ONE_HOUR;

					fetchDashboardPoll(timeoutDuration);
				} else {
					setDashboardFetchTimeoutId(null);
				}

				if (password !== '' && passwordNeeded) {
					trackDashboardOpened('Y');
				}
			})
			.catch((error) => {
				console.error(error);

				const errorStatus = error?.response?.status || -1;

				if (errorStatus === 401) {
					if (passwordNeeded) {
						trackDashboardIncorrectPassword();
						setPassword('');
					}

					// Incorrect password
					setDashboardValid(true);
					setPasswordNeeded(true);
					setShowDashboard(false);
				} else if (errorStatus === 404) {
					// Asset expired
					setDashboardValid(true);
					setExpired(true);
				} else {
					console.error('Unrecognised error', error);
					setDashboardValid(false);
				}
			})
			.finally(() => {
				// We have successfully requested information on the dashboard
				setDashboardChecked(true);
			});
	}, [baseUrl, password, passwordNeeded, sharedDashboardUuid]);

	const fetchDashboardPoll = useCallback((timeoutDuration: number) => {
		const timeoutId = setTimeout(() => fetchAndProcessDashboardJSON(true), timeoutDuration);

		setDashboardFetchTimeoutId(timeoutId);
	}, [fetchAndProcessDashboardJSON]);

	const windowFocused = useCallback(() => {
		if (showDashboard && dashboardJSON !== null && windowBlurTimestampRef.current !== null) {
			const timeDifference = Date.now() - windowBlurTimestampRef.current;

			windowBlurTimestampRef.current = null;

			if (timeDifference > REFETCH_CANCEL_BUFFER_TIME) {
				if (dashboardFetchTimeoutId) {
					clearTimeout(dashboardFetchTimeoutId);
				}

				fetchAndProcessDashboardJSON(true);
			}
		}
	}, [dashboardFetchTimeoutId, windowBlurTimestampRef.current, showDashboard, dashboardJSON]);

	const windowBlurred = useCallback(() => {
		if (dashboardFetchTimeoutId) {
			windowBlurTimestampRef.current = Date.now();
		}

	}, [dashboardFetchTimeoutId]);

	useEffect(() => {
		setMinutesSinceLastFetch(0);

		if (!lastUpdatedIntervalId) {
			const newIntervalId = setInterval(updateMinutesSinceLastFetch, ONE_MINUTE);

			setLastUpdatedIntervalId(newIntervalId);
		}

		return () => {
			if (lastUpdatedIntervalId) {
				clearInterval(lastUpdatedIntervalId);
				setLastUpdatedIntervalId(null);
			}
		};
	}, [dashboardJSON, lastUpdatedIntervalId]);

	useEffect(() => {
		if (checkIfPasswordIsValid(password)) {
			fetchAndProcessDashboardJSON();
		}
	}, [password]);

	useEffect(() => {
		window.addEventListener('focus', windowFocused);
		window.addEventListener('blur', windowBlurred);

		return () => {
			window.removeEventListener('focus', windowFocused);
			window.removeEventListener('blur', windowBlurred);
		};
	}, [windowBlurred, windowFocused]);

	useEffect(() => {
		const handleBundleError = (event) => {
			const error = event.detail?.error;

			if (error.status === 401) {
				setPasswordNeeded(true);
				setShowDashboard(false);
				setPassword('');
			} else if (error.status === 404) {
				setExpired(true);
			}
		};

		window.addEventListener('bundle:error', handleBundleError);

		fetchAndProcessDashboardJSON();

		return () => {
			if (dashboardFetchTimeoutId) {
				clearTimeout(dashboardFetchTimeoutId);
			}

			abortController?.abort();

			window.removeEventListener('bundle:error', handleBundleError);
		};
	}, []);

	// Show the dashboard!
	if (showDashboard && dashboardJSON !== null) {
		return <Dashboard
			baseUrl={baseUrl}
			dashboardJSON={dashboardJSON}
			minutesSinceLastFetch={minutesSinceLastFetch}
			password={password}
		/>;
	}

	// Have we asked the SDK about the dashboard yet?
	if (dashboardChecked) {
		// Is the dashboard in the system?
		if (dashboardValid) {
			// Has the dashboard expired?
			if (expired) {
				// Showing expired screen
				return <Expired />;
			} else if (passwordNeeded) {
				// Showing password required screen
				const hasIncorrectPassword = (passwordNeeded && password !== '');
				return <Password hasIncorrectPassword={hasIncorrectPassword} onSubmit={handlePasswordSubmission} />;
			} else {
				// Showing passwordless screen
				return <Passwordless onSubmit={handlePasswordlessSubmission} />;
			}
		} else {
			// Showing error screen
			return <Error />;
		}
	} else {
		// Still checking!
		return <Loader />;
	}
};

export { SharedDashboardContainer };
